import { useMemo } from 'react';
import { useTheme } from '@/hooks/useTheme';

/**
 * Єдиний стандарт графіків кабінету.
 *
 * Виведений з еталонів, які НЕ чіпаємо: «Ціни» (src/pages/market/prices) і
 * «Прогноз» (src/pages/forecast/redesign). Усі числа нижче — дослівно ті, що
 * там: сітка 3-3 без вертикалей, осі без ліній і засічок, тік 10px кольором
 * --text-dim, підпис осі 11px кольором --text-muted, тултіп на --surface-solid
 * з радіусом 10, лінії 2.5 без крапок з activeDot r=4, стовпці з fillOpacity
 * 0.18, обведенням 1px і maxBarSize 26.
 *
 * Навіщо хук, а не константи: recharts пише кольори у SVG-атрибути, а вони не
 * резолвлять var(--…). Токени доводиться діставати через getComputedStyle, і
 * перерахунок прив'язаний до isDark — інакше при перемиканні теми серії
 * лишаються старими (§8 дизайн-контракту).
 *
 * Легенда навмисно НЕ recharts-івська: <Legend> не піддається токенізації.
 * Використовуйте ChartLegend із цього ж модуля.
 */
export function useChartTheme() {
  const { isDark } = useTheme();

  return useMemo(() => {
    const css = getComputedStyle(document.documentElement);
    const v = (n: string, fb: string) => css.getPropertyValue(n).trim() || fb;

    const grid = v('--line', isDark ? 'rgba(255,255,255,0.08)' : '#E4E5E7');
    const dim = v('--text-dim', isDark ? '#7E868E' : '#9AA0A6');
    const muted = v('--text-muted', isDark ? '#C9CCCE' : '#5E656D');
    const text = v('--text', isDark ? '#F2F2F2' : '#28323C');
    const surface = v('--surface-solid', isDark ? '#2F3A45' : '#FFFFFF');

    /** Палітра серій за догмою §2–§3. Червоного тут немає навмисно. */
    const series = {
      ok: v('--ok', isDark ? '#4CAF6A' : '#2E7D46'),
      ok2: v('--ok-2', isDark ? '#40C094' : '#1F8A6B'),
      warn: v('--warn', isDark ? '#E0A24A' : '#C77A1B'),
      err: v('--err', isDark ? '#E5484D' : '#CC181B'),
      s1: v('--series-1', '#D23538'),
      s2: v('--series-2', isDark ? '#C9CCCE' : '#5E656D'),
      s3: v('--series-3', isDark ? '#C56467' : '#A6575B'),
      s4: v('--series-4', isDark ? '#5E656D' : '#C9CCCE'),
      s5: v('--series-5', '#98272F'),
      s6: v('--series-6', isDark ? '#A7242B' : '#862A32'),
    };

    return {
      isDark,
      grid, dim, muted, text, surface, series,

      /** <CartesianGrid {...ct.gridProps} /> */
      gridProps: { strokeDasharray: '3 3', stroke: grid, vertical: false } as const,

      /** <XAxis {...ct.axis} /> — те саме для YAxis. */
      axis: {
        tick: { fontSize: 10, fill: dim },
        axisLine: false as const,
        tickLine: false as const,
      },

      /** Підпис осі: <YAxis label={ct.axisLabel('Ціна, грн/МВт·год')} /> */
      axisLabel: (value: string, angle: -90 | 90 = -90) => ({
        value,
        angle,
        position: angle === -90 ? ('insideLeft' as const) : ('insideRight' as const),
        style: { fontSize: 11, fill: muted, textAnchor: 'middle' as const },
      }),

      /** <Tooltip {...ct.tooltip} /> */
      tooltip: {
        contentStyle: {
          background: surface,
          border: `1px solid ${grid}`,
          borderRadius: 10,
          fontSize: 12,
          color: text,
        },
        labelStyle: { color: text, fontWeight: 700 },
      },

      /** Курсор тултіпа: лінійні графіки — тонка лінія, стовпчикові — заливка. */
      cursorLine: { stroke: grid } as const,
      cursorBar: { fill: grid } as const,

      /** <Line {...ct.line} stroke={ct.series.s3} /> */
      line: {
        type: 'monotone' as const,
        strokeWidth: 2.5,
        dot: false as const,
        activeDot: { r: 4 },
        connectNulls: true,
        isAnimationActive: false,
      },

      /** Тонша лінія другого плану (план, довідкові значення). */
      lineThin: {
        type: 'monotone' as const,
        strokeWidth: 1.6,
        dot: false as const,
        connectNulls: true,
        isAnimationActive: false,
      },

      /** <Bar {...ct.bar} fill={c} stroke={c} /> */
      bar: {
        fillOpacity: 0.18,
        strokeWidth: 1,
        maxBarSize: 26,
        isAnimationActive: false,
      },
    };
  }, [isDark]);
}

export type ChartTheme = ReturnType<typeof useChartTheme>;
